/**
 * Comparison Panel Component
 * 
 * Side-by-side comparison view for selected studies.
 * Shows metrics table with highlighting for best/worst values.
 */

import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpRight,
  Calendar,
  Crown,
  Layers,
  Minus,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Trophy,
  X,
} from "lucide-react";

import { formatStrictWithApprox, parseApproxConfigFromToml } from "@/components/results/utils";
import { objectiveShortLabel } from "@/utils/objectiveLabel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import {
  type StudyWithMetadata,
  formatAge,
  getFreshnessColorClass,
  isPartOfParallelGroup,
  getParallelVersionIndicator,
} from "@/utils/studyGrouping";

// =============================================================================
// Types
// =============================================================================

interface ComparisonPanelProps {
  isOpen: boolean;
  studies: StudyWithMetadata[];
  onClose: () => void;
  onStudyClick: (studyId: string) => void;
}

interface MetricConfig {
  key: string;
  label: string;
  icon: React.ReactNode;
  getValue: (study: StudyWithMetadata) => number | null;
  format: (value: number | null) => string;
  // AIE-319/403: optional override that receives the full study so the
  // formatter can pull additional fields (e.g. total_trades + toml_config
  // for the approximate-real Sortino/Sharpe parenthetical). Wins over
  // `format` when provided.
  formatStudy?: (study: StudyWithMetadata) => string;
  higherIsBetter: boolean;
  colorThresholds?: {
    good: number;
    warning: number;
  };
}

// =============================================================================
// Constants
// =============================================================================

const METRICS: MetricConfig[] = [
  {
    key: "best_value",
    label: "Best Value",
    icon: <Trophy className="h-4 w-4 text-yellow-500" />,
    getValue: (s) => s.best_value ?? null,
    format: (v) => (v != null ? v.toFixed(4) : "—"),
    // AIE-319 + AIE-405: render the strict best with the AIE-319 approximate-real
    // parenthetical AND prefix the objective function type so mixed-objective
    // comparisons are self-describing (e.g. "Sortino: 0.6888 (~1.4)" /
    // "Sharpe: 0.5512 (~1.5)"). Same prefix applies whether both studies use
    // the same objective function or different ones, so the row format stays
    // consistent without any conditional rendering.
    formatStudy: (s) => {
      const value = formatStrictWithApprox(
        s.best_value,
        s.total_trades,
        parseApproxConfigFromToml(s.toml_config),
      );
      if (value === "N/A") return value;
      return `${objectiveShortLabel(s.objective_function)}: ${value}`;
    },
    higherIsBetter: true,
  },
  {
    key: "win_rate",
    label: "Win Rate",
    icon: <Target className="h-4 w-4 text-green-500" />,
    getValue: (s) => s.win_rate ?? null,
    format: (v) => (v != null ? `${(v * 100).toFixed(1)}%` : "—"),
    higherIsBetter: true,
    colorThresholds: { good: 0.55, warning: 0.5 },
  },
  // AIE-403/404 follow-up: hardcoded "Sharpe Ratio" row removed.
  // It always rendered s.sharpe_ratio regardless of the user's chosen objective
  // (mislabeled for Sortino studies) and its value coincidentally tracked the
  // Best Value parenthetical for typical strategies, making it read like a
  // confusing duplicate. Headline "Best Value" with the AIE-319 parenthetical
  // already conveys the unscaled value of the chosen objective. AIE-404 can
  // later reintroduce an objective-aware row (or show both Sortino+Sharpe
  // explicitly) once the labeling gap is fully addressed.
  {
    key: "max_drawdown",
    label: "Max Drawdown",
    icon: <TrendingDown className="h-4 w-4 text-red-500" />,
    getValue: (s) => s.max_drawdown ?? null,
    format: (v) => (v != null ? `${(v * 100).toFixed(1)}%` : "—"),
    higherIsBetter: false, // Lower is better for drawdown
    colorThresholds: { good: 0.1, warning: 0.2 },
  },
  {
    key: "profit_factor",
    label: "Profit Factor",
    icon: <TrendingUp className="h-4 w-4 text-purple-500" />,
    getValue: (s) => s.profit_factor ?? null,
    format: (v) => (v != null ? v.toFixed(2) : "—"),
    higherIsBetter: true,
    colorThresholds: { good: 1.5, warning: 1.2 },
  },
  {
    key: "total_trades",
    label: "Total Trades",
    icon: <Target className="h-4 w-4 text-cyan-500" />,
    getValue: (s) => s.total_trades ?? null,
    format: (v) => (v != null ? v.toString() : "—"),
    higherIsBetter: true, // More trades = more data
  },
  {
    key: "n_trials",
    label: "Trials",
    icon: <Sparkles className="h-4 w-4 text-orange-500" />,
    getValue: (s) => s.n_trials ?? null,
    format: (v) => (v != null ? v.toString() : "—"),
    higherIsBetter: true,
  },
  {
    key: "age",
    label: "Age (days)",
    icon: <Calendar className="h-4 w-4 text-dark-400" />,
    getValue: (s) => s.ageInDays,
    format: (v) => (v != null ? `${v}d` : "—"),
    higherIsBetter: false, // Newer is better
  },
];

// =============================================================================
// Main Component
// =============================================================================

export function ComparisonPanel({
  isOpen,
  studies,
  onClose,
  onStudyClick,
}: ComparisonPanelProps) {
  // Check if all studies are from the same parallel group
  const parallelGroupInfo = useMemo(() => {
    if (studies.length < 2) return null;

    const firstGroupId = studies[0]?.batch_id;
    if (!firstGroupId) return null;

    const allSameGroup = studies.every((s) => s.batch_id === firstGroupId);
    if (!allSameGroup) return null;

    return {
      parallel_group_id: firstGroupId,
      parallel_total: studies[0]?.batch_total ?? studies.length,
      isCompleteGroup: studies.length === (studies[0]?.batch_total ?? 0),
    };
  }, [studies]);

  // Calculate best/worst values for each metric
  const metricRankings = useMemo(() => {
    const rankings = new Map<string, { best: number | null; worst: number | null }>();

    for (const metric of METRICS) {
      const values = studies
        .map((s) => metric.getValue(s))
        .filter((v): v is number => v != null);

      if (values.length === 0) {
        rankings.set(metric.key, { best: null, worst: null });
        continue;
      }

      const sorted = [...values].sort((a, b) => a - b);
      const bestVal = metric.higherIsBetter ? sorted[sorted.length - 1] : sorted[0];
      const worstVal = metric.higherIsBetter ? sorted[0] : sorted[sorted.length - 1];
      rankings.set(metric.key, {
        best: bestVal ?? null,
        worst: worstVal ?? null,
      });
    }

    return rankings;
  }, [studies]);

  // AIE-405 follow-up: detect mixed-objective comparisons. When the selected
  // studies use different objective functions (Sortino vs Sharpe), the
  // Best Value row's head-to-head color coding would be misleading because
  // the two strict scores aren't directly comparable. The "Sortino:" /
  // "Sharpe:" cell prefix already tells the user which is which; we just
  // suppress the green/red so we don't imply a winner.
  const mixedObjectives = useMemo(() => {
    if (studies.length < 2) return false;
    const seen = new Set<string>();
    for (const s of studies) {
      seen.add(s.objective_function ?? "sortino");
      if (seen.size > 1) return true;
    }
    return false;
  }, [studies]);

  // Find overall winner (most "best" metrics)
  const overallWinner = useMemo(() => {
    const scores: Record<string, number> = {};
    
    for (const study of studies) {
      scores[study.study_id] = 0;
    }

    for (const metric of METRICS) {
      const ranking = metricRankings.get(metric.key);
      const bestValue = ranking?.best;
      if (bestValue == null) continue;

      for (const study of studies) {
        const value = metric.getValue(study);
        const currentScore = scores[study.study_id];
        if (value === bestValue && currentScore !== undefined) {
          scores[study.study_id] = currentScore + 1;
        }
      }
    }

    let winner: string | null = null;
    let maxScore = 0;
    for (const [studyId, score] of Object.entries(scores)) {
      if (score > maxScore) {
        maxScore = score;
        winner = studyId;
      }
    }

    return winner;
  }, [studies, metricRankings]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 bottom-4 top-20 md:inset-x-8 md:bottom-8 md:top-24 z-50"
          >
            <div className="h-full bg-dark-900 rounded-xl border border-dark-700 shadow-2xl flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-dark-800">
                <div className="flex items-center gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      {parallelGroupInfo && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 text-brand-purple">
                                <Layers className="h-5 w-5" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              Comparing parallel study versions from the same group
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {parallelGroupInfo ? "Parallel Study Comparison" : "Comparison View"}
                    </h2>
                    <p className="text-sm text-dark-400">
                      {parallelGroupInfo ? (
                        <>
                          Comparing{" "}
                          <span className="text-brand-purple font-medium">
                            {studies.length}
                          </span>{" "}
                          of {parallelGroupInfo.parallel_total} parallel versions
                        </>
                      ) : (
                        <>Comparing {studies.length} studies</>
                      )}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-6">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    {/* Study headers */}
                    <thead>
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-dark-400 bg-dark-800/50 rounded-tl-lg sticky left-0 z-10 min-w-[140px]">
                          Metric
                        </th>
                        {studies.map((study, index) => {
                          const versionIndicator = getParallelVersionIndicator(study);
                          const isFromParallelGroup = isPartOfParallelGroup(study);

                          return (
                            <th
                              key={study.study_id}
                              className={cn(
                                "text-left p-3 bg-dark-800/50 min-w-[200px]",
                                index === studies.length - 1 && "rounded-tr-lg"
                              )}
                            >
                              <div className="flex items-center gap-2">
                                {overallWinner === study.study_id && (
                                  <Crown className="h-4 w-4 text-yellow-500" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => onStudyClick(study.study_id)}
                                      className="font-bold text-white hover:text-blue-400 transition-colors truncate block text-left tracking-tight"
                                    >
                                      {study.study_name.split('_').slice(0, 3).join(' ').toUpperCase().replace(/^SAMPLE /, "") || `Study ${study.study_id.slice(0, 8)}`}
                                    </button>
                                    {isFromParallelGroup && versionIndicator && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Badge
                                              variant="outline"
                                              className="text-[10px] px-1.5 py-0 border-brand-purple/50 text-brand-purple bg-brand-purple/10"
                                            >
                                              {versionIndicator}
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            Version {study.batch_index} of {study.batch_total} parallel studies
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-dark-500">
                                      {study.metadata.symbol} • {study.metadata.timeframe}
                                    </span>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "text-xs border",
                                        getFreshnessColorClass(study.freshness)
                                      )}
                                    >
                                      {formatAge(study.ageInDays)}
                                    </Badge>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 flex-shrink-0"
                                  onClick={() => onStudyClick(study.study_id)}
                                >
                                  <ArrowUpRight className="h-4 w-4" />
                                </Button>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>

                    {/* Metric rows */}
                    <tbody>
                      {METRICS.map((metric, rowIndex) => {
                        const ranking = metricRankings.get(metric.key);

                        return (
                          <tr
                            key={metric.key}
                            className={rowIndex % 2 === 0 ? "bg-dark-900/50" : "bg-dark-800/20"}
                          >
                            <td className="p-3 sticky left-0 z-10 bg-inherit">
                              <div className="flex items-center gap-2 text-sm text-dark-300">
                                {metric.icon}
                                <span>{metric.label}</span>
                              </div>
                            </td>
                            {studies.map((study) => {
                              const value = metric.getValue(study);
                              const hasVariation = ranking != null && ranking.best !== ranking.worst;
                              // AIE-405 follow-up: suppress winner/loser coloring on the
                              // Best Value row when objective functions differ across the
                              // selected studies (the strict scores aren't directly comparable).
                              const suppressColor =
                                metric.key === "best_value" && mixedObjectives;
                              const isBest =
                                !suppressColor &&
                                value != null &&
                                ranking != null &&
                                value === ranking.best &&
                                hasVariation;
                              const isWorst =
                                !suppressColor &&
                                value != null &&
                                ranking != null &&
                                value === ranking.worst &&
                                studies.length > 1 &&
                                hasVariation;

                              return (
                                <td key={study.study_id} className="p-3">
                                  <MetricCell
                                    value={value}
                                    formattedValue={
                                      metric.formatStudy
                                        ? metric.formatStudy(study)
                                        : metric.format(value)
                                    }
                                    isBest={isBest}
                                    isWorst={isWorst}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="mt-6 flex flex-wrap items-center gap-4 md:gap-6 text-xs text-dark-500">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500/30 border border-green-500" />
                    <span>Best value</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500/30 border border-red-500" />
                    <span>Worst value</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-yellow-500" />
                    <span>Overall winner (most best values)</span>
                  </div>
                  {parallelGroupInfo && (
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 border-brand-purple/50 text-brand-purple bg-brand-purple/10"
                      >
                        v1/3
                      </Badge>
                      <span>Parallel study version</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// =============================================================================
// Metric Cell Component
// =============================================================================

interface MetricCellProps {
  value: number | null;
  formattedValue: string;
  isBest: boolean;
  isWorst: boolean;
}

function MetricCell({
  value,
  formattedValue,
  isBest,
  isWorst,
}: MetricCellProps) {
  if (value == null) {
    return (
      <span className="text-dark-600 flex items-center gap-1">
        <Minus className="h-3 w-3" />
        N/A
      </span>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-2 py-1 rounded",
        isBest && "bg-green-500/10 border border-green-500/30",
        isWorst && "bg-red-500/10 border border-red-500/30"
      )}
    >
      {isBest && (
        <ArrowUp className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
      )}
      {isWorst && (
        <ArrowDown className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
      )}
      <span
        className={cn(
          "font-medium",
          isBest && "text-green-400",
          isWorst && "text-red-400",
          !isBest && !isWorst && "text-dark-200"
        )}
      >
        {formattedValue}
      </span>
    </div>
  );
}

export default ComparisonPanel;

